import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";

import { useStatusModal } from "../context/StatusModalContext";
import useApi from "./useApi";

import {
  scheduleTypes,
  initalFormData,
  validationNeeded,
  billingPeriod,
  paymentTypes,
  canCancelSchedule,
} from "../Components/CreateSchedule/constants";

import { generateUniqueId } from "../store/GlobalVarriables";
import {
  combineToUTC,
  capitalize,
  getDecodedToken,
  isValidEmail,
  isOnlyNumbers,
  removeEmptyKeys,
  getChangedValues,
} from "../utils/helpers";

const useSchedule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scheduleId } = useParams();
  const { post, get, del, patch, loading } = useApi();
  const { showStatusModal } = useStatusModal();
  const decodedToken = getDecodedToken();
  const timeZone = DateTime.local();
  const isScheduleEdit = !!scheduleId;

  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps, setTotalSteps] = useState(5);
  const [selectedScheduleType, setSelectedScheduleType] = useState("");
  const [initialValues, setInitialValues] = useState(initalFormData);
  const [initilaDayAndTime, setInitialDays] = useState([]);
  const [formData, setFormData] = useState(initalFormData);
  const [address, setAddress] = useState({});
  const [currency, setCurrency] = useState("USD");
  const [oneTimeEventId, setOneTimeEventId] = useState("");
  const [inviteInputValue, setInviteInputValue] = useState("");
  const [invitedCustomers, setInvitedCustomers] = useState([]);
  const [selectedDayAndTime, setSelectedDayAndTime] = useState({});
  const [daysChanged, setDaysChanged] = useState(false);
  const [errors, setErrors] = useState({});
  const [maxMembersHasError, setMaxMembersHasError] = useState(false);
  const [warningModalToggle, setWarningModalToggle] = useState("");

  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const data = await get(`/BusinessAccount/${decodedToken.buid}`);
        if (data) {
          setAddress({
            city: data.city,
            district: data.district,
            address: data.address,
          });
          setCurrency(data.currency);
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (decodedToken.buid) fetchAddress();
  }, [decodedToken.buid, get]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const data = await get(`/Schedule/${scheduleId}`);
        if (data) {
          const hours = Math.floor(data.duration / 60);
          const minutes = data.duration - hours * 60;
          const effectiveFrom = format(data.effectiveFrom, "yyyy-MM-dd");

          const eventDate = data.event
            ? format(parseISO(data.event?.eventDate), "yyyy-MM-dd")
            : "";
          const eventTime = data.event
            ? format(parseISO(data.event?.eventDate), "HH:mm")
            : "";

          const userData = {
            scheduleName: data.description || "",
            maxMembers: data.maxMembers || "",
            duration: data.duration || "",
            durationMin: minutes || "",
            durationHours: hours || "",
            startDate: effectiveFrom || "",
            price: data.price || "",
            priceType: data.financialDetails.pricingType || "",
            canCancelSchedule: data.customerCanCancel
              ? canCancelSchedule.yes
              : canCancelSchedule.no,
            cancelationGrace: data.cancellationGraceHours || "",
            billingPeriod: data.financialDetails.billingPeriod || "",
            ofSessionBilling: data.financialDetails.billingEventCount || "",
            paymentType: data.financialDetails.paymentType || "",
            ofSessionPrePay: data.financialDetails.prepaymentEventCount || "",
            eventDate: eventDate || "",
            eventTime: eventTime || "",
          };

          const scheduleDayAndTime = data?.scheduleDays.reduce((acc, obj) => {
            acc[obj.dayOfWeek.toLowerCase()] = obj.timeOnly;
            return acc;
          }, {});

          const scheduleType = data.isOneTime
            ? scheduleTypes.oneTime
            : data.groupSchedule
              ? scheduleTypes.group
              : scheduleTypes.individual;

          setTotalSteps(scheduleType === scheduleTypes.oneTime ? 4 : 5);
          setSelectedScheduleType(scheduleType);
          setOneTimeEventId(data?.event?.id);
          setSelectedDayAndTime(scheduleDayAndTime);
          setInitialDays(data.scheduleDays);
          setInitialValues(userData);
          setFormData(userData);
          setCurrentStep(2);
        }
      } catch (error) {
        console.error(error);
      }
    };
    if (isScheduleEdit) fetchDetails();
  }, [scheduleId, isScheduleEdit, get]);

  const postInvitation = useCallback(
    async (invitations) => {
      const oneTimeData = {
        businessAccountId: decodedToken.buid,
        timezone: timeZone.zoneName,
        eventTime: combineToUTC(
          formData.eventDate,
          formData.eventTime,
          timeZone.zoneName,
        ),
        effectiveFrom: combineToUTC(
          formData.eventDate,
          formData.eventTime,
          timeZone.zoneName,
        ),
        duration: formData.duration,
        description: formData.scheduleName,
        price: +formData.price,
        ...address,
      };

      try {
        await post("/ApplicationEvent/oneTime", {
          schedule: oneTimeData,
          invitations,
        });
        setWarningModalToggle("modal-warning");
      } catch (e) {
        console.error(e);
      }
    },
    [decodedToken.buid, timeZone.zoneName, formData, address, post],
  );

  const createSchedule = useCallback(
    async (invitations) => {
      const commonScheduleData = {
        businessAccountId: decodedToken.buid,
        timezone: timeZone.zoneName,
        effectiveFrom: DateTime.fromISO(formData.startDate, {
          zone: timeZone.zoneName,
        })
          .toUTC()
          .toISO(),
        description: formData.scheduleName,
        groupSchedule: selectedScheduleType === scheduleTypes.group,
        isOneTime: false,
        maxMembers:
          selectedScheduleType === scheduleTypes.group
            ? +formData.maxMembers
            : 1,
        duration: formData.duration,
        customerCanCancel: formData.canCancelSchedule === canCancelSchedule.yes,
        cancellationGraceHours:
          formData.canCancelSchedule === canCancelSchedule.yes
            ? +formData.cancelationGrace
            : 0,
        price: +formData.price,
        scheduleDays: Object.entries(selectedDayAndTime).map(
          ([dayOfWeek, timeOnly]) => ({
            timeOnly,
            dayOfWeek: capitalize(dayOfWeek),
          }),
        ),
      };

      const commonFinancialData = {
        payerOption: "Individual",
        pricingType: formData.priceType,
        billingPeriod: formData.billingPeriod,
        billingEventCount:
          formData.billingPeriod === billingPeriod.sessinoBased
            ? +formData.ofSessionBilling
            : null,
        paymentType:
          formData.billingPeriod === billingPeriod.monthEnd
            ? paymentTypes.postPaid
            : formData.billingPeriod === billingPeriod.monthStart
              ? paymentTypes.prePaid
              : formData.billingPeriod === billingPeriod.sessinoBased
                ? formData.paymentType
                : null,
        prepaymentEventCount:
          formData.paymentType === paymentTypes.prePaid &&
          formData.billingPeriod === billingPeriod.sessinoBased
            ? +formData.ofSessionPrePay
            : null,
      };

      try {
        await post("/Schedule/Invite", {
          schedule: {
            ...commonScheduleData,
            financialDetails: { ...commonFinancialData },
            location: { ...address },
          },
          invitations,
        });
        setWarningModalToggle("modal-warning");
      } catch (e) {
        console.error(e);
      }
    },
    [
      decodedToken.buid,
      timeZone.zoneName,
      formData,
      selectedScheduleType,
      selectedDayAndTime,
      address,
      post,
    ],
  );

  const editSchedule = useCallback(async () => {
    const changedValues = getChangedValues(formData, initialValues);

    if (selectedScheduleType === scheduleTypes.oneTime) {
      let changeHappened = false;
      const { eventDate, eventTime, ...rest } = changedValues;

      if (eventDate || eventTime) {
        const newEventDate = new Date(
          `${formData.eventDate} ${formData.eventTime}`,
        );
        try {
          await patch("/ApplicationEvent", {
            id: oneTimeEventId,
            eventDate: newEventDate.toISOString(),
            eventStatus: "Rescheduled",
          });
          changeHappened = true;
        } catch (e) {
          console.error(e);
        }
      }

      if (Object.keys(rest).length > 0) {
        const chagedData = {
          description: rest.scheduleName || undefined,
          duration: +rest.duration || undefined,
          price: +rest.price || undefined,
        };

        await patch("/Schedule", {
          id: scheduleId,
          ...removeEmptyKeys(chagedData),
        });
        changeHappened = true;
      }

      if (changeHappened) {
        showStatusModal(t("schedule_edit_success"), `/schedules`);
      }
      return;
    }

    const effectiveFrom =
      changedValues && !!changedValues.startDate
        ? DateTime.fromISO(changedValues.startDate, {
            zone: timeZone.zoneName,
          })
            .toUTC()
            .toISO()
        : undefined;

    const finalData = {
      effectiveFrom,
      description: changedValues.scheduleName || undefined,
      maxMembers: +changedValues.maxMembers || undefined,
      duration: +changedValues.duration || undefined,
      cancellationGraceHours:
        +changedValues.cancelationGrace >= 0
          ? +changedValues.cancelationGrace
          : undefined,
      customerCanCancel: changedValues.canCancelSchedule
        ? changedValues.canCancelSchedule === canCancelSchedule.yes
        : undefined,
      price: +changedValues.price || undefined,
      financialDetails: {
        pricingType: changedValues.priceType || undefined,
        billingPeriod: changedValues.billingPeriod || undefined,
        billingEventCount: +changedValues.ofSessionBilling || undefined,
        paymentType: changedValues.paymentType || undefined,
        prepaymentEventCount: +changedValues.ofSessionPrePay || undefined,
      },
    };

    const scheduleDays = Object.entries(selectedDayAndTime).map(
      ([dayOfWeek, timeOnly]) => ({ timeOnly, dayOfWeek }),
    );

    const deletedDays = initilaDayAndTime.filter(
      (initialItem) =>
        !scheduleDays.some(
          (selecteditem) =>
            selecteditem.dayOfWeek === initialItem.dayOfWeek.toLowerCase(),
        ),
    );

    const addedDays = scheduleDays.filter(
      (selecteditem) =>
        !initilaDayAndTime.some(
          (initialItem) =>
            initialItem.dayOfWeek.toLowerCase() === selecteditem.dayOfWeek,
        ),
    );

    const modifiedDays = scheduleDays.flatMap((selecteditem) => {
      const matchingItem = initilaDayAndTime.find(
        (initialItem) =>
          initialItem.dayOfWeek.toLowerCase() === selecteditem.dayOfWeek &&
          initialItem.timeOnly !== selecteditem.timeOnly,
      );
      if (matchingItem) {
        return { ...selecteditem, id: matchingItem.id };
      }
      return [];
    });

    try {
      const dataToPass = removeEmptyKeys(finalData);
      let daysChanged = false;

      if (deletedDays.length > 0) {
        deletedDays.forEach((item) => {
          del(`/ScheduleDay/${item.id}`);
        });
        daysChanged = true;
      }

      if (addedDays.length > 0) {
        addedDays.forEach((item) => {
          post("/ScheduleDay", { ...item, scheduleId });
        });
        daysChanged = true;
      }

      if (modifiedDays.length > 0) {
        modifiedDays.forEach((item) => {
          patch("/ScheduleDay", item);
        });
        daysChanged = true;
      }

      if (dataToPass) {
        const response = await patch("/Schedule", {
          id: scheduleId,
          ...dataToPass,
        });
        if (response) {
          showStatusModal(t("schedule_edit_success"), `/schedules`);
        }
      } else if (daysChanged) {
        showStatusModal(t("schedule_edit_success"), `/schedules`);
      }
    } catch (err) {
      console.error(err);
    }
  }, [
    formData,
    initialValues,
    selectedScheduleType,
    selectedDayAndTime,
    initilaDayAndTime,
    scheduleId,
    oneTimeEventId,
    timeZone.zoneName,
    patch,
    post,
    del,
    showStatusModal,
    t,
  ]);

  const handleValidate = useCallback(
    (fields, step) => {
      let errorsObj = {};
      if (fields?.length) {
        fields.forEach((field) => {
          if (field === "selectedDays") {
            errorsObj["selectedDays"] =
              Object.keys(selectedDayAndTime).length === 0;
          } else if (!formData[field] || formData[field] === "0") {
            errorsObj[field] = true;
          }
        });
      }

      if (
        selectedScheduleType === scheduleTypes.group &&
        formData.maxMembers < 2
      ) {
        setMaxMembersHasError(true);
        return;
      }

      if (
        selectedScheduleType === scheduleTypes.individual &&
        formData.canCancelSchedule === canCancelSchedule.yes
      ) {
        errorsObj.cancelationGrace = !formData.cancelationGrace;
      }

      if (
        [scheduleTypes.group, scheduleTypes.individual].includes(
          selectedScheduleType,
        ) &&
        currentStep === 3
      ) {
        if (formData.billingPeriod === billingPeriod.sessinoBased) {
          errorsObj.ofSessionBilling = !formData.ofSessionBilling;
        }
        if (
          formData.paymentType === paymentTypes.prePaid &&
          formData.billingPeriod === billingPeriod.sessinoBased
        ) {
          errorsObj.ofSessionPrePay = !formData.ofSessionPrePay;
        }
      }

      if (
        inviteInputValue.length > 5 &&
        (isValidEmail(inviteInputValue) || isOnlyNumbers(inviteInputValue))
      ) {
        errorsObj.inviteInput = true;
      }

      const finalErrors = Object.fromEntries(
        Object.entries(errorsObj).filter(([_, val]) => val),
      );
      setErrors(finalErrors);
      if (Object.keys(finalErrors).length === 0) {
        if (isScheduleEdit && step === totalSteps - 2) {
          setCurrentStep(totalSteps);
        } else {
          setCurrentStep((prev) => prev + 1);
        }

        setErrors({});
      }
    },
    [
      formData,
      selectedDayAndTime,
      selectedScheduleType,
      inviteInputValue,
      isScheduleEdit,
      totalSteps,
      currentStep,
    ],
  );

  const handleNext = useCallback(() => {
    if (selectedScheduleType && validationNeeded[selectedScheduleType]) {
      handleValidate(
        validationNeeded[selectedScheduleType][`step${currentStep}`],
        currentStep,
      );
    }
  }, [currentStep, selectedScheduleType, handleValidate]);

  const handleBack = useCallback(() => {
    // back from edit review to jump over invitations step
    if (isScheduleEdit && currentStep === totalSteps) {
      setCurrentStep(totalSteps - 2);
    } else if (currentStep > 2) setCurrentStep((prev) => prev - 1);
    else navigate(-1);
    setErrors({});
  }, [currentStep, isScheduleEdit, totalSteps, navigate]);

  const handleSelectScheduleType = useCallback(
    (type) => {
      setSelectedScheduleType(type);
      setCurrentStep(2);
      setTotalSteps(type === scheduleTypes.oneTime ? 4 : 5);
      setFormData((prev) => ({
        ...prev,
        scheduleName: t(
          type === scheduleTypes.oneTime
            ? "createSchedule.onetimeFlow.schedule_name_default"
            : type === scheduleTypes.group
              ? "createSchedule.groupFlow.schedule_name_default"
              : "createSchedule.individualFlow.schedule_name_default",
        ),
      }));

      setInitialValues((prev) => ({
        ...prev,
        scheduleName: t(
          type === scheduleTypes.oneTime
            ? "createSchedule.onetimeFlow.schedule_name_default"
            : type === scheduleTypes.group
              ? "createSchedule.groupFlow.schedule_name_default"
              : "createSchedule.individualFlow.schedule_name_default",
        ),
      }));
      setSelectedDayAndTime({});
      setInvitedCustomers([]);
      setInviteInputValue("");
    },
    [t],
  );

  const handleInviteCustomer = useCallback((customer) => {
    if (!customer.trim()) return;
    setInvitedCustomers((prev) => [
      ...prev,
      { contact: customer.trim(), id: generateUniqueId() },
    ]);
    setInviteInputValue("");
    setErrors((prev) => ({ ...prev, inviteInput: false }));
  }, []);

  const handleDeleteCustomer = useCallback((id) => {
    setInvitedCustomers((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const handleFormFieldChange = useCallback((e) => {
    const { name, value } = e.target;

    const resetErrorFieldName =
      name === "minutes" || name === "hours" ? "duration" : name;

    //reset errors
    setErrors((prev) => ({ ...prev, [resetErrorFieldName]: false }));
    if (name === "maxMembers") setMaxMembersHasError(false);

    //if not session based reset aditional fields
    if (name === "billingPeriod" && value !== billingPeriod.sessinoBased) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ofSessionBilling: "",
        ofSessionPrePay: "",
        paymentType:
          value === billingPeriod.monthStart
            ? paymentTypes.prePaid
            : paymentTypes.postPaid,
      }));
      return;
    }

    if (name === "minutes") {
      setFormData((prev) => ({
        ...prev,
        durationMin: value,
        duration: Number(prev.durationHours) * 60 + Number(value),
      }));
      return;
    }

    if (name === "hours") {
      setFormData((prev) => ({
        ...prev,
        durationHours: value,
        duration: Number(prev.durationMin) + Number(value) * 60,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectDay = useCallback((selected) => {
    setSelectedDayAndTime(selected);
    setDaysChanged(true);
    setErrors((prev) => ({ ...prev, selectedDays: false }));
  }, []);

  return {
    currentStep,
    totalSteps,
    selectedScheduleType,
    formData,
    formDataChanged:
      Object.keys(getChangedValues(formData, initialValues)).length > 0 ||
      daysChanged,
    address,
    currency,
    inviteInputValue,
    invitedCustomers,
    selectedDayAndTime,
    errors,
    maxMembersHasError,
    warningModalToggle,
    loading,
    setCurrentStep,
    setWarningModalToggle,
    handleBack,
    handleNext,
    handleFormFieldChange,
    handleSelectScheduleType,
    handleInviteCustomer,
    handleDeleteCustomer,
    setInviteInputValue,
    handleSelectDay,
    postInvitation,
    createSchedule,
    editSchedule,
    setFormData,
    setAddress,
    setCurrency,
  };
};

export default useSchedule;
